import { useEffect, useState } from "react";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectActiveWorkspace,
  setActiveWorkspace,
  setWorkspacePermissions,
} from "redux/authSlice";
import {
  fetchMyWorkspaces,
  fetchMyWorkspacePermissions,
} from "screens/Settings/Workspaces/WorkspacesServices";
import { Workspace } from "types/workspace";

/**
 * General active-workspace switcher for the top bar. Lists the workspaces the current user belongs
 * to (GET secure/workspace/mine). Renders nothing when the user is in no workspace, so it stays out
 * of the way for accounts that don't use workspaces.
 */
const WorkspaceSwitcher = () => {
  const dispatch = useAppDispatch();
  const activeWorkspaceId = useAppSelector(selectActiveWorkspace);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyWorkspaces()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setWorkspaces(list);
        // Auto-clear a stale active workspace: if the persisted activeWorkspaceId is no longer
        // in the user's list (removed from it / archived / deleted since last session), keep
        // sending it would put an invalid X-Workspace-Id on every scoped request. Reset to
        // Personal so the app is usable again.
        if (activeWorkspaceId && !list.some((w) => w.id === activeWorkspaceId)) {
          dispatch(setActiveWorkspace(""));
        }
      })
      .catch(() => {
        if (!cancelled) setWorkspaces([]);
      });
    // Effective permissions in the active workspace — used to gate scoped actions in the UI.
    // Switching workspace reloads the page, so fetching on mount also covers switches.
    fetchMyWorkspacePermissions()
      .then((perms) => {
        if (!cancelled) dispatch(setWorkspacePermissions(perms));
      })
      .catch(() => {
        // Fail CLOSED in a workspace context: leaving the default { scoped:false } state on
        // error makes hasWorkspacePermission fail open (gated actions look enabled). If an
        // active workspace is set but we couldn't confirm permissions, assume none.
        if (!cancelled && activeWorkspaceId) {
          dispatch(
            setWorkspacePermissions({ scoped: true, admin: false, permissions: [] }),
          );
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hide only when there's genuinely nothing to show: no workspaces AND no stale active id to
  // clear. If an active id is still set (mid auto-clear, or a race), keep the switcher visible so
  // the "Personal (no workspace)" option gives the user a manual escape hatch.
  if (!workspaces.length && !activeWorkspaceId) return null;

  const active = workspaces.find((w) => w.id === activeWorkspaceId);

  const pick = (id: string) => {
    if (id === (activeWorkspaceId || "")) {
      setAnchor(null);
      return;
    }
    dispatch(setActiveWorkspace(id));
    setAnchor(null);
    // Switching the active workspace changes the X-Workspace-Id header on every scoped request,
    // but pages already on screen won't re-fetch on their own. Redux state is persisted
    // synchronously (store.subscribe), so the new selection survives a reload — reload so the
    // current page comes back with data scoped to the newly selected workspace.
    window.location.reload();
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<WorkspacesOutlinedIcon fontSize="small" />}
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ mr: 2, textTransform: "none", maxWidth: 220 }}
        id="workspace-switcher-button"
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? "workspace-switcher-menu" : undefined}
        aria-label={`Active workspace: ${
          active ? active.name : "Personal"
        }. Change workspace`}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {active ? active.name : "Personal"}
        </Box>
      </Button>
      <Menu
        id="workspace-switcher-menu"
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        MenuListProps={{
          "aria-labelledby": "workspace-switcher-button",
          "aria-label": "Select active workspace",
        }}
      >
        <MenuItem selected={!activeWorkspaceId} onClick={() => pick("")}>
          Personal (no workspace)
        </MenuItem>
        {workspaces.map((w) => (
          <MenuItem
            key={w.id}
            selected={w.id === activeWorkspaceId}
            onClick={() => pick(w.id)}
          >
            {w.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default WorkspaceSwitcher;
